import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useToast } from '#app/components/Toasts/hooks/useToast.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useFeatureFlag } from '#app/hooks/useFeatureFlag.js';
import { useRequestMembership } from '#app/hooks/useRequestMembership.js';
import { useUserTypeSelector } from '#app/hooks/useUserTypeSelector.js';
import { useCallback } from 'react';

const analyticsSection = 'advanced_network_subscription_flow';

export const useAdvancedNetworkSubscriptionFlow = ({
  onSubscribed,
  onSubscriptionModalClosed,
  onUpsellModalClosed,
}: {
  onSubscribed?: () => void;
  onSubscriptionModalClosed?: () => void;
  onUpsellModalClosed?: () => void;
}) => {
  const toast = useToast();
  const currentUser = useCurrentUser();
  const { selectedUserType, toggleSelectedUserType } = useUserTypeSelector();
  const { fireRequestMembership } = useRequestMembership({
    customTitle: `Join our members-only network to access network management`,
    parentAnalyticsSection: analyticsSection,
    source: 'basic_network_page',
  });

  const onFeatureUnlocked = useCallback(
    ({ modalAction }) => {
      toast.success(
        "Welcome to the Grow plan. You've unlocked the advanced network.",
        {
          duration: 4_000,
        },
      );

      modalAction();
      onSubscribed?.();
    },
    [onSubscribed, toast],
  );

  const membersOnly = useFeatureFlag('membersOnly');
  const { track } = useAnalytics();

  const {
    closeModal: closeSubscriptionModal,
    isModalOpen: isSubscriptionModalOpen,
    openModal: openSubscriptionModal,
  } = useModal({
    identifier: 'ClientWorkspaceSubscriptionModal',
    modalProps: {
      analyticsProperties: {
        feature: 'advanced_network',
        source: 'basic_network_page',
      },
      headerCopyOverride: {
        subtitle:
          'Your payment information will be used for your team’s contractor payments and plan subscription.',
        title: 'Upgrade to access advanced talent lists',
      },
      initialView: 'CHECKOUT_GROW',
      onClose: onSubscriptionModalClosed
        ? () => {
            onSubscriptionModalClosed();
          }
        : undefined,
      onFeatureUnlocked: membersOnly
        ? undefined
        : () =>
            onFeatureUnlocked({
              modalAction: closeSubscriptionModal,
            }),
      preselectedBillingCycle: 'MONTHLY',
      targetFeatureToUnlock: 'CLIENT_ACCESS_NETWORK',
    },
  });

  const {
    closeModal: closeAdvancedNetworkSignUpModal,
    isModalOpen: isAdvancedNetworkSignUpModalOpen,
    openModal: openAdvancedNetworkSignUpModal,
  } = useModal({
    identifier: 'AdvancedNetworkUpgradeModal',
    modalProps: {
      onClick: () => {
        if (membersOnly) {
          track('button', {
            action: 'tapped',
            button_name: 'request_membership',
            section: analyticsSection,
          });
          fireRequestMembership();
        } else {
          openSubscriptionModal();
        }
      },
      onClose: onUpsellModalClosed
        ? () => {
            onUpsellModalClosed();
          }
        : undefined,
    },
  });

  const start = useCallback(() => {
    if (selectedUserType !== 'CLIENT' && currentUser) {
      toggleSelectedUserType({ skipToast: true });
    }

    openAdvancedNetworkSignUpModal();
  }, [
    currentUser,
    openAdvancedNetworkSignUpModal,
    selectedUserType,
    toggleSelectedUserType,
  ]);

  const close = useCallback(() => {
    if (isSubscriptionModalOpen) closeSubscriptionModal();
    if (isAdvancedNetworkSignUpModalOpen) closeAdvancedNetworkSignUpModal();
  }, [
    closeAdvancedNetworkSignUpModal,
    closeSubscriptionModal,
    isAdvancedNetworkSignUpModalOpen,
    isSubscriptionModalOpen,
  ]);

  return { close, start };
};
